<template>
  <!-- This hidden player's video/image element will be written to the global store -->
  <div
    ref="containerRef"
    style="
      position: absolute;
      top: -9999px;
      left: -9999px;
      width: 1px;
      height: 1px;
      overflow: hidden;
      visibility: hidden;
    ">
    <video
      ref="playerRef"
      autoplay
      playsinline
      muted
      style="width: 100%; height: 100%"></video>
    <!-- Separate video element for MSE so it doesn't fight with WebRTC's srcObject -->
    <video
      ref="mseVideoRef"
      autoplay
      playsinline
      muted
      style="width: 100%; height: 100%"></video>
    <!-- MJPEG Fallback Image -->
    <img
      ref="fallbackRef"
      style="width: 100%; height: 100%; display: none"
      alt="Stream Fallback"
      @error="handleFallbackError" />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { state } from '@/stores/deluge.js';

const props = defineProps({
  streamName: {
    type: String,
    default: 'preview',
  },
});

const playerRef = ref(null);
const mseVideoRef = ref(null);
const fallbackRef = ref(null);
const containerRef = ref(null);
let pc = null;
let _recreateHandler = null;
let retryTimeout = null;
let inFallbackMode = false; // Track if we're actually using MJPEG fallback
let streamGeneration = 0; // Incremented on each startStream to cancel stale attempts
let retryCount = 0; // Track consecutive retries for backoff
let mseWs = null;
let mseMediaSource = null;
let mseSourceBuffer = null;
let mseTimeout = null;
let mseActive = false;
let mseFailed = false; // MSE attempted and failed this generation
let mseStallTimer = null; // Polls video.currentTime to detect stalls
let mseLastTime = -1; // Last observed currentTime for stall detection
let mseStallCount = 0; // Consecutive stall detections before recovery

const getGo2rtcApi = () => {
  const url = `${window.location.origin}/api/go2rtc/webrtc?src=${props.streamName}`;
  console.log(
    '[Streamer] Connecting to:',
    url,
    'streamName:',
    props.streamName
  );
  return url;
};

// When both WebRTC and MSE have resolved (connected or failed), decide what to do.
// Called whenever either protocol's state changes.
function checkBothFailed() {
  const webrtcFailed = !pc || pc.connectionState === 'failed' || pc.connectionState === 'closed';
  if (webrtcFailed && mseFailed) {
    console.warn('[Streamer] Both WebRTC and MSE failed. Trying MJPEG...');
    startMJPEGFallback();
  }
}

// Handle MJPEG Load Error (e.g. Stream Offline) -> Retry Main Loop
const handleFallbackError = () => {
  // Only react if we're actually in fallback mode (not spurious errors from clearing src)
  if (!inFallbackMode) {
    console.debug('[Streamer] MJPEG error ignored - not in fallback mode');
    return;
  }

  // Don't restart if WebRTC video is already playing successfully
  const video = playerRef.value;
  if (video && video.srcObject && !video.paused && video.readyState >= 2) {
    console.debug('[Streamer] MJPEG error ignored - WebRTC video is playing');
    // Hide fallback image since WebRTC is working
    if (fallbackRef.value) fallbackRef.value.style.display = 'none';
    inFallbackMode = false;
    return;
  }

  console.warn(
    '[Streamer] MJPEG Fallback failed (Stream offline?). Retrying main loop...'
  );
  // Hide image
  if (fallbackRef.value) fallbackRef.value.style.display = 'none';
  inFallbackMode = false;

  // Clear any pending timeouts
  if (retryTimeout) clearTimeout(retryTimeout);
  // Backoff: 1s, 2s, 4s, 4s...
  const delay = Math.min(1000 * Math.pow(2, retryCount), 4000);
  retryCount++;
  retryTimeout = setTimeout(startStream, delay);
};

function cleanupMSE() {
  if (mseTimeout) { clearTimeout(mseTimeout); mseTimeout = null; }
  if (mseStallTimer) { clearInterval(mseStallTimer); mseStallTimer = null; }
  mseActive = false;
  mseLastTime = -1;
  mseStallCount = 0;
  if (mseWs) {
    try { mseWs.close(); } catch (e) { /* ignore */ }
    mseWs = null;
  }
  mseSourceBuffer = null;
  if (mseMediaSource && mseMediaSource.readyState === 'open') {
    try { mseMediaSource.endOfStream(); } catch (e) { /* ignore */ }
  }
  mseMediaSource = null;
}

// MSE/WebSocket — works through Cloudflare Tunnels and reverse proxies.
// Started in parallel with WebRTC. Uses a separate <video> element so both
// protocols can run simultaneously without fighting over src/srcObject.
// go2rtc requires a handshake: client sends {"type":"mse","value":"<codecs>"}
// after connecting, then go2rtc responds with codec info + binary fMP4 data.
function startMSE() {
  const gen = streamGeneration;

  console.log('[Streamer] Starting MSE (parallel with WebRTC)...');

  cleanupMSE();
  mseFailed = false;

  if (!window.MediaSource) {
    console.warn('[Streamer] MSE not supported');
    mseFailed = true;
    checkBothFailed();
    return;
  }

  const video = mseVideoRef.value;
  if (!video) {
    mseFailed = true;
    checkBothFailed();
    return;
  }

  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${wsProtocol}//${window.location.host}/api/go2rtc/ws?src=${props.streamName}`;
  console.log('[Streamer] MSE connecting to:', wsUrl);

  // Timeout: if MSE doesn't produce data within 10s, mark failed
  mseTimeout = setTimeout(() => {
    if (gen !== streamGeneration) return;
    console.warn('[Streamer] MSE timed out (10s).');
    cleanupMSE();
    mseFailed = true;
    checkBothFailed();
  }, 10000);

  mseMediaSource = new MediaSource();
  video.src = URL.createObjectURL(mseMediaSource);

  let pendingBuffers = [];
  let removing = false;

  // Seek video to the live edge (end of buffered data).
  // Called after tab returns from background or when stall is detected.
  // Aborts any in-flight SourceBuffer operation first to prevent
  // "still processing" errors that cause permanent stalls.
  function seekToLive() {
    try {
      if (mseSourceBuffer) {
        if (mseSourceBuffer.updating) {
          mseSourceBuffer.abort();
        }
        if (mseSourceBuffer.buffered.length > 0) {
          const end = mseSourceBuffer.buffered.end(mseSourceBuffer.buffered.length - 1);
          if (video.currentTime < end - 0.5) {
            console.debug('[Streamer] MSE seeking to live edge:', end.toFixed(1));
            video.currentTime = end;
          }
        }
      }
    } catch (e) { /* ignore */ }
  }

  function flushQueue() {
    if (!mseSourceBuffer || mseSourceBuffer.updating || removing || pendingBuffers.length === 0) return;

    // Cap pending queue: if too many frames buffered (e.g. background tab),
    // drop old ones to stay near live. Keep last ~5 chunks (roughly 1-2s of video).
    if (pendingBuffers.length > 50) {
      const dropped = pendingBuffers.length - 5;
      pendingBuffers.splice(0, dropped);
      console.debug(`[Streamer] MSE dropped ${dropped} queued chunks (background accumulation)`);
    }

    // Trim SourceBuffer beyond 30s to prevent unbounded memory growth
    try {
      if (mseSourceBuffer.buffered.length > 0) {
        const start = mseSourceBuffer.buffered.start(0);
        const end = mseSourceBuffer.buffered.end(mseSourceBuffer.buffered.length - 1);
        if (end - start > 30) {
          removing = true;
          mseSourceBuffer.remove(start, end - 10);
          return; // updateend will call flushQueue again
        }
      }
    } catch (e) { /* ignore */ }

    const data = pendingBuffers.shift();
    try {
      mseSourceBuffer.appendBuffer(data);
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        pendingBuffers.unshift(data);
        try {
          removing = true;
          const start = mseSourceBuffer.buffered.start(0);
          const end = mseSourceBuffer.buffered.end(mseSourceBuffer.buffered.length - 1);
          mseSourceBuffer.remove(start, end - 5);
        } catch (e2) { /* ignore */ }
      }
    }
  }

  function onMseUpdateEnd() {
    removing = false;
    flushQueue();
  }

  // SourceBuffer decode error — corrupted segment or codec mismatch.
  // This is unrecoverable for the current MSE session; restart cleanly.
  function onSourceBufferError() {
    if (gen !== streamGeneration) return;
    console.error('[Streamer] MSE SourceBuffer decode error — restarting');
    cleanupMSE();
    retryTimeout = setTimeout(startStream, 500);
  }

  // MediaSource can close unexpectedly (browser GC, codec issue, etc).
  // Detect and restart if this happens while we think we're still streaming.
  function onMediaSourceClose() {
    if (gen !== streamGeneration || !mseActive) return;
    console.warn('[Streamer] MediaSource closed unexpectedly — restarting');
    cleanupMSE();
    retryTimeout = setTimeout(startStream, 500);
  }
  mseMediaSource.addEventListener('sourceclose', onMediaSourceClose);

  // Stall detection: poll currentTime every 3s. If it hasn't advanced
  // despite mseActive being true, the video is stuck. Try seeking to
  // live edge first; if that fails twice in a row, restart the stream.
  function startStallDetection() {
    mseLastTime = -1;
    mseStallCount = 0;
    if (mseStallTimer) clearInterval(mseStallTimer);
    mseStallTimer = setInterval(() => {
      if (gen !== streamGeneration || !mseActive) return;
      const t = video.currentTime;
      if (mseLastTime >= 0 && t === mseLastTime && !video.paused) {
        mseStallCount++;
        console.warn(`[Streamer] MSE stall detected (${mseStallCount}): currentTime stuck at ${t.toFixed(1)}`);
        if (mseStallCount >= 3) {
          // Stuck for 9s+ — restart the whole stream
          console.warn('[Streamer] MSE stall unrecoverable, restarting...');
          cleanupMSE();
          retryTimeout = setTimeout(startStream, 500);
        } else {
          // Try to recover: seek to live edge and resume playback
          seekToLive();
          video.play().catch(() => {});
        }
      } else {
        mseStallCount = 0;
      }
      mseLastTime = t;
    }, 3000);
  }

  // Background tab handling: when the tab is hidden, the browser may
  // throttle/pause video decoding but the WebSocket keeps receiving.
  // On return, drop accumulated buffers and seek to live edge.
  function onVisibilityChange() {
    if (gen !== streamGeneration) return;
    if (document.visibilityState === 'visible' && mseActive) {
      console.debug('[Streamer] Tab visible again, syncing to live');
      // Drop accumulated pending data — it's all old now
      if (pendingBuffers.length > 5) {
        const dropped = pendingBuffers.length - 5;
        pendingBuffers.splice(0, dropped);
        console.debug(`[Streamer] Dropped ${dropped} queued chunks from background`);
      }
      // Seek to live edge after a short delay (let current append finish)
      setTimeout(() => {
        if (gen !== streamGeneration || !mseActive) return;
        seekToLive();
        video.play().catch(() => {});
      }, 200);
    }
  }
  document.addEventListener('visibilitychange', onVisibilityChange);

  const ws = new WebSocket(wsUrl);
  ws.binaryType = 'arraybuffer';
  mseWs = ws;

  ws.onopen = () => {
    if (gen !== streamGeneration) { ws.close(); return; }
    console.debug('[Streamer] MSE WebSocket connected, sending handshake');

    // go2rtc requires the client to send supported codecs before it starts streaming.
    // Build the codec list using MediaSource.isTypeSupported for accurate negotiation.
    const codecs = {};
    const types = [
      'avc1.640029', 'avc1.64002A', 'avc1.640033',  // H.264 High
      'avc1.4D4029', 'avc1.4D002A',                   // H.264 Main
      'avc1.42E01E', 'avc1.42E01F',                    // H.264 Baseline
      'hvc1.1.6.L153.B0',                              // H.265
    ];
    for (const codec of types) {
      if (MediaSource.isTypeSupported(`video/mp4; codecs="${codec}"`)) {
        codecs[codec] = true;
      }
    }
    // Also check audio codecs
    for (const codec of ['mp4a.40.2', 'opus', 'flac']) {
      if (MediaSource.isTypeSupported(`audio/mp4; codecs="${codec}"`)) {
        codecs[codec] = true;
      }
    }

    ws.send(JSON.stringify({
      type: 'mse',
      value: Object.keys(codecs).join(','),
    }));
  };

  ws.onmessage = (event) => {
    if (gen !== streamGeneration) { ws.close(); return; }

    if (typeof event.data === 'string') {
      // go2rtc sends JSON messages — codec info, errors, etc.
      let msg;
      try { msg = JSON.parse(event.data); } catch (e) { return; }

      if (msg.type === 'mse') {
        // Codec response — create the SourceBuffer
        console.debug('[Streamer] MSE codec:', msg.value);
        if (!mseSourceBuffer && mseMediaSource && mseMediaSource.readyState === 'open') {
          try {
            mseSourceBuffer = mseMediaSource.addSourceBuffer(msg.value);
            mseSourceBuffer.mode = 'segments';
            mseSourceBuffer.addEventListener('updateend', onMseUpdateEnd);
            mseSourceBuffer.addEventListener('error', onSourceBufferError);
            flushQueue();
          } catch (e) {
            console.error('[Streamer] MSE addSourceBuffer failed:', e);
            cleanupMSE();
            mseFailed = true;
            checkBothFailed();
          }
        }
      } else if (msg.type === 'error') {
        console.error('[Streamer] MSE error from go2rtc:', msg.value);
      }
    } else {
      // Binary fMP4 data
      pendingBuffers.push(event.data);
      flushQueue();

      // First binary data received — MSE is streaming.
      if (mseTimeout) {
        clearTimeout(mseTimeout);
        mseTimeout = null;
        mseActive = true;
        retryCount = 0;
        video.play().catch(() => {});

        // Point playerRef to MSE video (WebRTC may switch it back later)
        state.playerRef = video;
        state.streamMode = 'mse';
        startStallDetection();
        console.debug('[Streamer] MSE stream active');
      }
    }
  };

  ws.onerror = () => {
    if (gen !== streamGeneration) return;
    console.error('[Streamer] MSE WebSocket error');
  };

  ws.onclose = () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (gen !== streamGeneration) return;
    console.warn('[Streamer] MSE WebSocket closed');

    if (mseActive) {
      // Was streaming — retry full cycle
      cleanupMSE();
      const delay = Math.min(500 * Math.pow(2, retryCount), 3000);
      retryCount++;
      retryTimeout = setTimeout(startStream, delay);
    } else {
      // Never started streaming — mark failed
      cleanupMSE();
      mseFailed = true;
      checkBothFailed();
    }
  };
}

// MJPEG fallback — last resort for maximum compatibility
function startMJPEGFallback() {
  console.warn(
    '[Streamer] Switching to MJPEG fallback.'
  );
  if (pc) {
    pc.close();
    pc = null;
  }
  cleanupMSE();

  // If we are already in fallback mode logic somehow, ensure clean start
  if (retryTimeout) clearTimeout(retryTimeout);

  // Mark that we're now in fallback mode (so error handler knows to react)
  inFallbackMode = true;

  // Hide video, show image
  if (playerRef.value) playerRef.value.style.display = 'none';
  if (mseVideoRef.value) mseVideoRef.value.style.display = 'none';
  if (fallbackRef.value) {
    fallbackRef.value.style.display = 'block';

    const mjpegUrl = `${window.location.origin}/api/go2rtc/stream.mjpeg?src=${props.streamName}`;
    console.log('[Streamer] Loading fallback URL:', mjpegUrl);

    fallbackRef.value.src = mjpegUrl;

    // Update global state to point to the image element
    state.playerRef = fallbackRef.value;
    state.streamMode = 'mjpeg';
  }
}

async function startStream() {
  // Increment generation so any in-flight stale attempts are discarded
  const gen = ++streamGeneration;

  // We're starting fresh, not in fallback mode
  inFallbackMode = false;
  mseFailed = false;

  // Reset UI to Video mode
  if (playerRef.value) playerRef.value.style.display = 'block';
  if (mseVideoRef.value) mseVideoRef.value.style.display = 'block';
  if (fallbackRef.value) {
    fallbackRef.value.style.display = 'none';
    fallbackRef.value.src = ''; // Stop downloading MJPEG
  }
  // Reset state to WebRTC video element
  state.playerRef = playerRef.value;

  // Clear timers
  if (retryTimeout) {
    clearTimeout(retryTimeout);
    retryTimeout = null;
  }

  // Clean up previous connections
  cleanupMSE();
  if (pc) {
    pc.close();
    pc = null;
  }

  // === Start both WebRTC and MSE simultaneously ===
  // Each uses its own <video> element so they don't conflict.
  // WebRTC is preferred (lower latency). If WebRTC connects,
  // MSE is cleaned up. If WebRTC fails, MSE is already streaming.

  pc = new RTCPeerConnection();

  pc.oniceconnectionstatechange = () => {
    if (gen !== streamGeneration) return;
    if (pc.iceConnectionState === 'failed') {
      console.warn('[Streamer] ICE failed (firewall/NAT).');
      if (pc) { pc.close(); pc = null; }
      // If MSE is running, it's already handling video.
      // If MSE also failed, checkBothFailed() already ran or will run.
      if (!mseWs && !mseActive) {
        checkBothFailed();
      }
    }
  };

  pc.onconnectionstatechange = () => {
    if (gen !== streamGeneration) return; // stale
    console.debug('[Streamer] connection state', pc.connectionState);
    if (pc.connectionState === 'failed') {
      console.warn('[Streamer] WebRTC connection failed (ICE). Likely firewall/tunnel.');
      if (pc) { pc.close(); pc = null; }
      if (!mseWs && !mseActive) {
        checkBothFailed();
      }
    } else if (pc.connectionState === 'disconnected') {
      console.warn('WebRTC disconnected (Stream stopped?). Retrying...');
      // Backoff: 500ms, 1s, 2s, 3s...
      const delay = Math.min(500 * Math.pow(2, retryCount), 3000);
      retryCount++;
      retryTimeout = setTimeout(startStream, delay);
    } else if (pc.connectionState === 'connected') {
      // WebRTC connected — always prefer it over MSE (lower latency).
      if (mseWs || mseActive) {
        console.debug('[Streamer] WebRTC connected — switching from MSE to WebRTC');
      }
      cleanupMSE();
      // Point global state to the WebRTC video element
      state.playerRef = playerRef.value;
      retryCount = 0;
      state.streamMode = 'webrtc';
    }
  };

  pc.addTransceiver('video', { direction: 'recvonly' });

  pc.ontrack = (event) => {
    if (gen !== streamGeneration) return; // stale
    console.debug('[Streamer] ontrack - received video track');
    // ontrack fires from setRemoteDescription (SDP processing) — BEFORE ICE
    // connects and media flows. Attach to the WebRTC video element but don't
    // switch state.playerRef yet — wait for connectionState === 'connected'.
    if (playerRef.value) {
      playerRef.value.srcObject = event.streams[0];
      playerRef.value.play().catch((e) => console.error('Auto-play failed', e));
    }
  };

  // Start MSE immediately in parallel — uses mseVideoRef, no conflict
  startMSE();

  try {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const res = await fetch(getGo2rtcApi(), {
      method: 'POST',
      body: pc.localDescription.sdp,
    });

    // Discard if a newer startStream() was called while we were waiting
    if (gen !== streamGeneration) {
      console.debug('[Streamer] Discarding stale WebRTC response');
      return;
    }

    if (!res.ok) {
      // 404 = stream doesn't exist, 500 = source not ready (SSB still starting)
      // Both are transient — retry WebRTC directly instead of falling back to MJPEG
      throw new Error('ServerError');
    }

    const sdp = await res.text();
    await pc.setRemoteDescription({ type: 'answer', sdp });
  } catch (e) {
    if (gen !== streamGeneration) return; // stale

    console.error('WebRTC negotiation failed:', e);

    if (e.message === 'ServerError') {
      // go2rtc returned 4xx/5xx — source not ready yet. Retry everything.
      // MSE will also fail if the stream doesn't exist, so restart both.
      console.warn('[Streamer] Stream not ready (server error). Retrying...');
      const delay = Math.min(500 * Math.pow(2, retryCount), 3000);
      retryCount++;
      retryTimeout = setTimeout(startStream, delay);
    } else {
      // Actual WebRTC/network failure — close PC, let MSE handle it
      if (pc) { pc.close(); pc = null; }
      if (!mseWs && !mseActive) {
        checkBothFailed();
      }
    }
  }
}

onMounted(() => {
  state.playerRef = playerRef.value;
  startStream();

  const handler = async () => {
    // console.debug('Force-recreate-streamer event');
    await startStream();
  };

  if (containerRef.value && containerRef.value.addEventListener) {
    containerRef.value.addEventListener('force-recreate-streamer', handler);
    _recreateHandler = handler;
  }

  watch(
    () => props.streamName,
    () => startStream()
  );
});

onUnmounted(() => {
  if (retryTimeout) clearTimeout(retryTimeout);
  cleanupMSE();
  if (pc) {
    pc.close();
    pc = null;
  }
  if (containerRef.value && _recreateHandler) {
    containerRef.value.removeEventListener(
      'force-recreate-streamer',
      _recreateHandler
    );
  }
});
</script>
